import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';

const BASE = process.env.REACT_APP_API_BASE_URL;

function StatBadge({ label, value, color }) {
    return (
        <div className={`flex flex-col items-center px-3 py-2 rounded-xl ${color}`}>
            <span className="text-lg font-bold">{value}</span>
            <span className="text-xs opacity-70">{label}</span>
        </div>
    );
}

function StudentDetailPanel({ student, onClose, onMessage }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setDetail(null);
        setLoading(true);
        apiClient(`${BASE}/messages.php?action=student_detail&id=${student.id}`)
            .then(r => r.json())
            .then(j => { if (j.success) setDetail(j.student); })
            .finally(() => setLoading(false));
    }, [student.id]);

    const initial = (student.name || '?')[0].toUpperCase();

    return (
        <div className="fixed inset-0 z-[110] flex justify-end" onClick={onClose}>
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
            <div
                className="relative z-10 w-full max-w-sm bg-white dark:bg-gray-900 h-full shadow-2xl flex flex-col overflow-y-auto"
                onClick={e => e.stopPropagation()}>

                <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-white/10">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">Fiche élève</h3>
                    <button onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 transition-colors">
                        ✕
                    </button>
                </div>

                <div className="p-5 flex flex-col gap-5">
                    {/* Avatar + identité */}
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold shrink-0">
                            {initial}
                        </div>
                        <div>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{student.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{student.email}</p>
                        </div>
                    </div>

                    {loading ? (
                        <p className="text-sm text-gray-400">Chargement…</p>
                    ) : detail && (
                        <>
                            {/* Stats */}
                            <div className="flex gap-2">
                                <StatBadge label="Exercices" value={detail.skills_done}     color="bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300" />
                                <StatBadge label="Flashcards"   value={detail.flashcards_count} color="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300" />
                                <StatBadge label="Quizzes"      value={detail.quizzes_count}    color="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300" />
                            </div>

                            {/* Tuteurs */}
                            {detail.tuteurs?.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Tuteurs</p>
                                    <div className="flex flex-col gap-1.5">
                                        {detail.tuteurs.map((t, i) => (
                                            <div key={i} className="flex items-center justify-between px-3 py-2 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-500/20">
                                                <p className="text-xs font-semibold text-blue-800 dark:text-blue-200">{t.subject_name}</p>
                                                <p className="text-xs text-blue-600 dark:text-blue-400">{t.tuteur_name}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Classes */}
                            {detail.classes?.length > 0 && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Classes</p>
                                    <div className="flex flex-col gap-1.5">
                                        {detail.classes.map((c, i) => (
                                            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-500/20">
                                                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />
                                                <div>
                                                    <p className="text-sm font-semibold text-teal-800 dark:text-teal-200">{c.classe_nom}</p>
                                                    <p className="text-xs text-teal-600 dark:text-teal-400">{c.professeur_name}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-gray-400">
                                Inscrit le {new Date(detail.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                        </>
                    )}

                    {/* Action */}
                    <button
                        onClick={() => onMessage(student.id)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-colors">
                        ✉ Envoyer un message
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ProfesseurClasses() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [classes, setClasses]         = useState([]);
    const [loading, setLoading]         = useState(true);
    const [selectedClass, setSelected]  = useState(null);
    const [students, setStudents]       = useState([]);
    const [studentsLoading, setSL]      = useState(false);
    const [showNewClass, setShowNew]    = useState(false);
    const [newClassName, setNewName]    = useState('');
    const [creating, setCreating]       = useState(false);
    const [showAddStudent, setShowAdd]  = useState(false);
    const [addForm, setAddForm]         = useState({ email: '', name: '', password: '' });
    const [addErr, setAddErr]           = useState('');
    const [adding, setAdding]           = useState(false);
    const [busyRemove, setBusyRemove]   = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);

    const loadClasses = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/professeur_classes.php?action=list_classes`);
            const json = await res.json();
            if (json.success) setClasses(json.classes);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { loadClasses(); }, [loadClasses]);

    // Ouvrir automatiquement un élève via ?open=studentId (ex. depuis la messagerie)
    useEffect(() => {
        const openId = parseInt(searchParams.get('open') || '0');
        if (!openId) return;
        apiClient(`${BASE}/messages.php?action=student_detail&id=${openId}`)
            .then(r => r.json())
            .then(j => {
                if (j.success) setSelectedStudent(j.student);
                setSearchParams({}, { replace: true });
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStudents = useCallback(async (classeId) => {
        setSL(true);
        setStudents([]);
        try {
            const res  = await apiClient(`${BASE}/professeur_classes.php?action=class_students&classe_id=${classeId}`);
            const json = await res.json();
            if (json.success) setStudents(json.students);
        } finally { setSL(false); }
    }, []);

    const selectClass = (cls) => {
        setSelected(cls);
        setShowAdd(false);
        setAddErr('');
        loadStudents(cls.id);
    };

    const handleCreateClass = async (e) => {
        e.preventDefault();
        if (!newClassName.trim()) return;
        setCreating(true);
        try {
            const res  = await apiClient(`${BASE}/professeur_classes.php?action=create_class`, {
                method: 'POST',
                body: JSON.stringify({ nom: newClassName.trim() }),
            });
            const json = await res.json();
            if (json.success) {
                setNewName('');
                setShowNew(false);
                await loadClasses();
            }
        } finally { setCreating(false); }
    };

    const handleDeleteClass = async (cls) => {
        if (!window.confirm(`Supprimer la classe "${cls.nom}" et retirer tous ses élèves ?`)) return;
        await apiClient(`${BASE}/professeur_classes.php?action=delete_class`, {
            method: 'POST',
            body: JSON.stringify({ id: cls.id }),
        });
        if (selectedClass?.id === cls.id) setSelected(null);
        await loadClasses();
    };

    const handleAddStudent = async (e) => {
        e.preventDefault();
        setAddErr('');
        setAdding(true);
        try {
            const res  = await apiClient(`${BASE}/professeur_classes.php?action=add_student`, {
                method: 'POST',
                body: JSON.stringify({ classe_id: selectedClass.id, ...addForm }),
            });
            const json = await res.json();
            if (json.success) {
                setAddForm({ email: '', name: '', password: '' });
                setShowAdd(false);
                await loadStudents(selectedClass.id);
                await loadClasses();
            } else {
                setAddErr(json.message || 'Erreur.');
            }
        } finally { setAdding(false); }
    };

    const handleRemove = async (studentId) => {
        if (!window.confirm('Retirer cet élève de la classe ?')) return;
        setBusyRemove(studentId);
        try {
            await apiClient(`${BASE}/professeur_classes.php?action=remove_student`, {
                method: 'POST',
                body: JSON.stringify({ classe_id: selectedClass.id, student_id: studentId }),
            });
            await loadStudents(selectedClass.id);
            await loadClasses();
        } finally { setBusyRemove(null); }
    };

    const openMessage = (studentId) => {
        setSelectedStudent(null);
        navigate(`/messagerie?to=${studentId}`);
    };

    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mes classes</h2>
                <button onClick={() => setShowNew(v => !v)}
                    className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium transition-colors">
                    + Nouvelle classe
                </button>
            </div>

            {showNewClass && (
                <form onSubmit={handleCreateClass}
                    className="mb-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 p-4 flex gap-3 items-center">
                    <input
                        type="text"
                        placeholder="Nom de la classe (ex : Terminale A)"
                        value={newClassName}
                        onChange={e => setNewName(e.target.value)}
                        required
                        autoFocus
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <button type="button" onClick={() => { setShowNew(false); setNewName(''); }}
                        className="px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                        Annuler
                    </button>
                    <button type="submit" disabled={creating}
                        className="px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                        {creating ? 'Création…' : 'Créer'}
                    </button>
                </form>
            )}

            <div className="flex gap-6 items-start">
                {/* Liste des classes */}
                <div className="w-64 shrink-0 flex flex-col gap-2">
                    {classes.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-8">Aucune classe pour l'instant.</p>
                    )}
                    {classes.map(cls => (
                        <div key={cls.id}
                            onClick={() => selectClass(cls)}
                            className={`cursor-pointer rounded-xl border p-4 transition-all ${
                                selectedClass?.id === cls.id
                                    ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 dark:border-teal-500/50'
                                    : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-white/10 hover:border-teal-300 dark:hover:border-teal-500/30'
                            }`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white text-sm">{cls.nom}</p>
                                    <p className="text-xs text-gray-400 mt-0.5">
                                        {cls.student_count || 0} élève{cls.student_count !== '1' ? 's' : ''}
                                        {parseInt(cls.pending_count) > 0 && (
                                            <span className="ml-1.5 text-amber-500">· {cls.pending_count} en attente</span>
                                        )}
                                    </p>
                                    {/* Code de classe */}
                                    <div className="flex items-center gap-1 mt-2"
                                        onClick={e => e.stopPropagation()}
                                        title="Code à partager avec vos élèves">
                                        <span className="font-mono text-xs font-bold tracking-widest text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 rounded select-all">
                                            {cls.join_code}
                                        </span>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(cls.join_code)}
                                            className="text-gray-300 dark:text-gray-600 hover:text-teal-500 transition-colors text-xs p-0.5"
                                            title="Copier le code">
                                            📋
                                        </button>
                                    </div>
                                </div>
                                <button
                                    onClick={e => { e.stopPropagation(); handleDeleteClass(cls); }}
                                    className="text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors text-xs p-1 rounded shrink-0"
                                    title="Supprimer la classe">
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Détail classe sélectionnée */}
                {selectedClass && (
                    <div className="flex-1 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/5">
                            <h3 className="font-semibold text-gray-900 dark:text-white">{selectedClass.nom}</h3>
                            <button onClick={() => setShowAdd(v => !v)}
                                className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-xs font-medium transition-colors">
                                + Ajouter un élève
                            </button>
                        </div>

                        {/* Formulaire d'ajout */}
                        {showAddStudent && (
                            <form onSubmit={handleAddStudent}
                                className="px-5 py-4 border-b border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-white/[0.02] flex flex-col gap-3">
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Entrez l'email de l'élève. S'il n'a pas encore de compte, remplissez tous les champs pour en créer un.
                                </p>
                                {addErr && (
                                    <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">{addErr}</p>
                                )}
                                <div className="flex flex-wrap gap-3">
                                    <input type="email" placeholder="Email *" value={addForm.email}
                                        onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} required
                                        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                                    <input type="text" placeholder="Prénom Nom (si nouveau compte)" value={addForm.name}
                                        onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
                                        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                                    <input type="password" placeholder="Mot de passe (si nouveau compte)" value={addForm.password}
                                        onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
                                        className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500" />
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button type="button" onClick={() => { setShowAdd(false); setAddErr(''); setAddForm({ email: '', name: '', password: '' }); }}
                                        className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-white/10 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                        Annuler
                                    </button>
                                    <button type="submit" disabled={adding}
                                        className="px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                                        {adding ? 'Ajout…' : 'Ajouter'}
                                    </button>
                                </div>
                            </form>
                        )}

                        {/* Tableau des élèves */}
                        {studentsLoading ? (
                            <div className="text-center py-10 text-gray-400 text-sm">Chargement…</div>
                        ) : students.length === 0 ? (
                            <div className="text-center py-10 text-gray-400 text-sm">Aucun élève dans cette classe.</div>
                        ) : (
                            <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[500px]">
                                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 text-xs uppercase">
                                    <tr>
                                        <th className="p-3 text-left">Nom</th>
                                        <th className="p-3 text-left">Email</th>
                                        <th className="p-3 text-center">Statut</th>
                                        <th className="p-3 text-center">Exercices</th>
                                        <th className="p-3 text-center">Flashcards</th>
                                        <th className="p-3 text-center">Quizzes</th>
                                        <th className="p-3 text-center"></th>
                                        <th className="p-3 text-center"></th>
                                        <th className="p-3 text-center"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                                    {students.map(s => (
                                        <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-white/5">
                                            <td className="p-3 font-medium text-gray-800 dark:text-gray-200">{s.name}</td>
                                            <td className="p-3 text-gray-500 text-xs">{s.email}</td>
                                            <td className="p-3 text-center">
                                                {s.status === 'pending' ? (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300">En attente</span>
                                                ) : (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-300">Accepté</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-center text-gray-600 dark:text-gray-400">{s.skills_done}</td>
                                            <td className="p-3 text-center text-gray-600 dark:text-gray-400">{s.flashcards_count}</td>
                                            <td className="p-3 text-center text-gray-600 dark:text-gray-400">{s.quizzes_count}</td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => setSelectedStudent(s)}
                                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors px-2 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
                                                    Voir
                                                </button>
                                            </td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => navigate(`/messagerie?to=${s.id}`)}
                                                    className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors px-2 py-1 rounded hover:bg-cyan-50 dark:hover:bg-cyan-900/20">
                                                    Message
                                                </button>
                                            </td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => handleRemove(s.id)}
                                                    disabled={busyRemove === s.id}
                                                    className="text-xs text-red-400 hover:text-red-600 dark:hover:text-red-300 disabled:opacity-40 transition-colors px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                                                    {busyRemove === s.id ? '…' : 'Retirer'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </div>
                )}

                {!selectedClass && classes.length > 0 && (
                    <div className="flex-1 flex items-center justify-center py-20 text-gray-400 text-sm">
                        Sélectionnez une classe pour voir ses élèves
                    </div>
                )}
            </div>

            {/* Panneau de détail élève */}
            {selectedStudent && (
                <StudentDetailPanel
                    student={selectedStudent}
                    onClose={() => setSelectedStudent(null)}
                    onMessage={openMessage}
                />
            )}
        </div>
    );
}
